/**
 * Google Ads / gtag.js analytics helpers.
 *
 * The base gtag.js snippet + `config('AW-18106732930')` is loaded from
 * public/index.html. This module provides thin, idempotent wrappers to fire
 * conversion events without polluting page components.
 */

const GOOGLE_ADS_ID = 'AW-18106732930';
// Conversion action label ("Compra") provided by Google Ads.
// Together with the account ID → send_to: 'AW-18106732930/SJJhCMbLwZ8cEIKj-7lD'.
const PURCHASE_CONVERSION_LABEL = 'SJJhCMbLwZ8cEIKj-7lD';

// Track transaction_ids we already reported in this session so a page
// refresh (or the polling loop in PaymentSuccess) never double-counts a
// conversion.
const REPORTED_KEY = 'gads_reported_transactions';

function alreadyReported(transactionId) {
  if (!transactionId) return false;
  try {
    const raw = sessionStorage.getItem(REPORTED_KEY) || '[]';
    return JSON.parse(raw).includes(transactionId);
  } catch {
    return false;
  }
}

function markReported(transactionId) {
  if (!transactionId) return;
  try {
    const raw = sessionStorage.getItem(REPORTED_KEY) || '[]';
    const arr = JSON.parse(raw);
    if (!arr.includes(transactionId)) {
      arr.push(transactionId);
      sessionStorage.setItem(REPORTED_KEY, JSON.stringify(arr));
    }
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * Fire a Google Ads purchase conversion event.
 *
 * Sends TWO gtag events for full coverage:
 *   1. `conversion` with `send_to: AW-XXXX/LABEL` — this is what Google Ads
 *      counts against the specific "Compra" conversion action.
 *   2. `purchase` — enhanced e-commerce data (value, currency) that Google
 *      Analytics / GA4 aggregates for reporting.
 *
 * Both events share the same `transaction_id` so duplicates are prevented.
 *
 * @param {Object} params
 * @param {string} params.transactionId - Unique order ID (dedup key)
 * @param {number} params.value         - Order total in the currency
 * @param {string} [params.currency]    - ISO 4217, defaults to 'EUR'
 * @param {string} [params.conversionLabel] - Overrides the default label
 */
export function trackPurchase({ transactionId, value, currency = 'EUR', conversionLabel }) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (!transactionId || !value || value <= 0) return;
  if (alreadyReported(transactionId)) return;

  const label = conversionLabel || PURCHASE_CONVERSION_LABEL;
  const sendTo = `${GOOGLE_ADS_ID}/${label}`;

  try {
    // 1) Google Ads conversion (what shows up in Ads > Conversions)
    window.gtag('event', 'conversion', {
      send_to: sendTo,
      transaction_id: String(transactionId),
      value: Number(value),
      currency,
    });
    // 2) Enhanced e-commerce purchase (feeds GA/GA4 reports)
    window.gtag('event', 'purchase', {
      send_to: sendTo,
      transaction_id: String(transactionId),
      value: Number(value),
      currency,
    });
    markReported(transactionId);
  } catch {
    /* silent — analytics must never break checkout */
  }
}
